import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const BulkActionsPanel = ({ onBulkCreate, isVisible, onClose }) => {
  const [bulkData, setBulkData] = useState({
    brandName: '',
    rateType: '',
    stores: [],
    quantity: 1,
    description: ''
  });

  const [errors, setErrors] = useState({});

  // Mock data
  const stores = [
    { value: 'store-001', label: 'Dhaka Central Mall - Store A1' },
    { value: 'store-002', label: 'Chittagong Plaza - Store B2' },
    { value: 'store-003', label: 'Sylhet Shopping Center - Store C3' },
    { value: 'store-004', label: 'Rajshahi Market - Store D4' },
    { value: 'store-005', label: 'Khulna Complex - Store E5' }
  ];

  const brands = [
    { value: 'coca-cola', label: 'Coca-Cola' },
    { value: 'pepsi', label: 'Pepsi' },
    { value: 'samsung', label: 'Samsung' },
    { value: 'lg', label: 'LG Electronics' },
    { value: 'unilever', label: 'Unilever' }
  ];

  const rateTypes = [
    { value: 'window-display', label: 'Window Display' },
    { value: 'billboard', label: 'Billboard' },
    { value: 'led-screen', label: 'LED Screen' },
    { value: 'in-store-static', label: 'In-Store Static' },
    { value: 'gondola-branding', label: 'Gondola Branding' }
  ];

  const handleInputChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!bulkData?.brandName) {
      newErrors.brandName = 'Please select a brand';
    }
    if (!bulkData?.rateType) {
      newErrors.rateType = 'Please select a rate type';
    }
    if (!bulkData?.stores?.length) {
      newErrors.stores = 'Please select at least one store';
    }
    if (!bulkData?.quantity || bulkData?.quantity < 1) {
      newErrors.quantity = 'Quantity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onBulkCreate(bulkData);
      setBulkData({
        brandName: '',
        rateType: '',
        stores: [],
        quantity: 1,
        description: ''
      });
      onClose();
    }
  };

  const handleReset = () => {
    setBulkData({
      brandName: '',
      rateType: '',
      stores: [],
      quantity: 1,
      description: ''
    });
    setErrors({});
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Copy" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Bulk Create Items</h2>
              <p className="text-sm text-muted-foreground">Create branding items across multiple stores</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Brand Selection */}
            <Select
              label="Brand Name"
              placeholder="Select brand"
              options={brands}
              value={bulkData?.brandName}
              onChange={(value) => handleInputChange('brandName', value)}
              error={errors?.brandName}
              required
              searchable
            />

            {/* Rate Type Selection */}
            <Select
              label="Branding Type"
              placeholder="Select branding type"
              options={rateTypes}
              value={bulkData?.rateType}
              onChange={(value) => handleInputChange('rateType', value)}
              error={errors?.rateType}
              required
              searchable
            />
          </div>

          {/* Store Selection */}
          <Select
            label="Target Stores"
            placeholder="Select stores"
            options={stores}
            value={bulkData?.stores}
            onChange={(value) => handleInputChange('stores', value)}
            error={errors?.stores}
            required
            multiple
            searchable
            clearable
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quantity */}
            <Input
              label="Quantity per Store"
              type="number"
              placeholder="Enter quantity"
              value={bulkData?.quantity}
              onChange={(e) => handleInputChange('quantity', parseInt(e?.target?.value) || 1)}
              error={errors?.quantity}
              required
              min={1}
            />

            {/* Description */}
            <Input
              label="Description (Optional)"
              type="text"
              placeholder="Additional notes"
              value={bulkData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />
          </div>

          {/* Preview */}
          {bulkData?.stores?.length > 0 && (
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Eye" size={16} />
                Preview ({bulkData?.stores?.length} items will be created)
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {bulkData?.stores?.map((storeId) => {
                  const store = stores?.find(s => s?.value === storeId);
                  return (
                    <div key={storeId} className="flex items-center justify-between text-sm bg-background rounded px-3 py-2">
                      <span className="text-foreground">{store?.label}</span>
                      <span className="text-muted-foreground">Qty: {bulkData?.quantity}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              iconName="RotateCcw"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Reset Form
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              iconName="Plus"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Create {bulkData?.stores?.length} Items
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkActionsPanel;